import { SeededRandom } from '../utils/SeededRandom';

export interface Biome {
    name: string;
    startHeight: number; // Y position where biome starts
    endHeight: number; // Y position where biome ends
    backgroundImage: string; // Background image for this biome
    platformStyle: 'wood' | 'stone' | 'cloud' | 'metal' | 'crystal' | 'organic';
    colorPalette: {
        primary: number; // Main platform color (hex)
        secondary: number; // Accent color
        ambient: number; // Ambient light color
    };
    platformTypes: ('rectangular' | 'arc' | 'spline' | 'branch' | 'ledge' | 'cloud')[];
    difficultyMultiplier: number; // How hard platforms are (1.0 = normal, 2.0 = 2x harder)
    visualElements: string[]; // Visual storytelling elements (e.g., "falling_leaves", "snowflakes")
    description: string; // For UI/tooltips
}

/**
 * Biome System - Creates cohesive themed zones as you climb
 * 
 * Inspired by:
 * - Getting Over It: Single continuous mountain with clear progression
 * - Downwell: Vertical shaft with consistent theme and color palette
 * - Worms: Themed environments that tell a story
 * 
 * Each biome represents a vertical zone with:
 * - Consistent visual style
 * - Matching platform aesthetics
 * - Progressive difficulty
 * - Environmental storytelling
 */
export class BiomeSystem {
    private biomes: Biome[];
    private rng: SeededRandom;
    private totalHeight: number;

    constructor(seed: number, totalHeight: number) {
        this.rng = new SeededRandom(seed);
        this.totalHeight = totalHeight;
        this.biomes = this.generateBiomes();
    }

    /**
     * Generate biome progression based on total height
     * Creates a vertical journey: Forest → Mountain → Sky → Space
     */
    private generateBiomes(): Biome[] {
        const biomeCount = Math.max(3, Math.floor(this.totalHeight / 1500)); // ~1 biome per 1500 units
        const biomeHeight = this.totalHeight / biomeCount;
        
        const biomeTemplates: Omit<Biome, 'startHeight' | 'endHeight'>[] = [
            // FOREST FLOOR - Pikuniku-inspired: Warm, earthy, cheerful
            {
                name: 'Forest Floor',
                backgroundImage: '', // Will be procedurally generated
                platformStyle: 'wood',
                colorPalette: {
                    primary: 0xD4A574, // Warm tan/beige (like Pikuniku's earthy tones)
                    secondary: 0x8FBC8F, // Soft sage green
                    ambient: 0xF0E68C // Light khaki (warm, inviting)
                },
                platformTypes: ['rectangular', 'branch', 'spline'],
                difficultyMultiplier: 0.8, // Easier at start
                visualElements: ['falling_leaves', 'moss'],
                description: 'The journey begins...'
            },
            // FOREST CANOPY - Brighter, more vibrant greens
            {
                name: 'Forest Canopy',
                backgroundImage: '',
                platformStyle: 'organic',
                colorPalette: {
                    primary: 0xB8860B, // Dark goldenrod (warmer brown)
                    secondary: 0x98FB98, // Pale green (cheerful)
                    ambient: 0xE0F6FF // Light sky blue (bright, airy)
                },
                platformTypes: ['branch', 'spline', 'rectangular'],
                difficultyMultiplier: 1.0,
                visualElements: ['falling_leaves', 'birds'],
                description: 'Climbing through the treetops...'
            },
            // MOUNTAIN BASE - Soft grays with warm undertones
            {
                name: 'Mountain Base',
                backgroundImage: '',
                platformStyle: 'stone',
                colorPalette: {
                    primary: 0xC0C0C0, // Silver gray (softer than before)
                    secondary: 0xD2B48C, // Tan (warm stone)
                    ambient: 0xF5F5DC // Beige (warm, not cold)
                },
                platformTypes: ['rectangular', 'ledge', 'arc'],
                difficultyMultiplier: 1.3,
                visualElements: ['rock_debris', 'wind'],
                description: 'The mountain rises...'
            },
            // MOUNTAIN PEAK - Cool but not harsh, still friendly
            {
                name: 'Mountain Peak',
                backgroundImage: '',
                platformStyle: 'stone',
                colorPalette: {
                    primary: 0xE6E6FA, // Lavender (soft, not harsh gray)
                    secondary: 0xFFF8DC, // Cornsilk (warm white)
                    ambient: 0xF0F8FF // Alice blue (soft, friendly)
                },
                platformTypes: ['ledge', 'rectangular', 'arc'],
                difficultyMultiplier: 1.6,
                visualElements: ['snowflakes', 'wind', 'ice'],
                description: 'The peak is near...'
            },
            // SKY / CLOUDS - Bright, cheerful pastels
            {
                name: 'Sky',
                backgroundImage: '',
                platformStyle: 'cloud',
                colorPalette: {
                    primary: 0xFFE4E1, // Misty rose (soft pink-white)
                    secondary: 0x87CEEB, // Sky blue (classic, cheerful)
                    ambient: 0xF0F8FF // Alice blue (bright, airy)
                },
                platformTypes: ['cloud', 'spline', 'arc'],
                difficultyMultiplier: 1.8,
                visualElements: ['clouds', 'wind'],
                description: 'Above the clouds...'
            },
            // AURORA / SPACE - Vibrant but not overwhelming
            {
                name: 'Aurora',
                backgroundImage: '',
                platformStyle: 'crystal',
                colorPalette: {
                    primary: 0xDDA0DD, // Plum (softer purple)
                    secondary: 0xAFEEEE, // Pale turquoise (bright, friendly)
                    ambient: 0xF0E6FF // Very light lavender (dreamy)
                },
                platformTypes: ['crystal', 'arc', 'spline'],
                difficultyMultiplier: 2.0,
                visualElements: ['aurora', 'stars', 'particles'],
                description: 'The aurora dances...'
            }
        ];

        const biomes: Biome[] = [];
        let currentHeight = 0;

        // Distribute biomes across the height
        for (let i = 0; i < biomeCount && i < biomeTemplates.length; i++) {
            const template = biomeTemplates[i];
            const startHeight = currentHeight;
            const endHeight = i === biomeCount - 1 
                ? this.totalHeight 
                : currentHeight + biomeHeight;

            biomes.push({
                ...template,
                startHeight,
                endHeight
            });

            currentHeight = endHeight;
        }

        return biomes;
    }

    /**
     * Get the biome for a given Y position
     */
    getBiomeAtHeight(y: number): Biome {
        // Y increases as you go up, so we need to invert
        const heightFromBottom = this.totalHeight - y;
        
        for (const biome of this.biomes) {
            if (heightFromBottom >= biome.startHeight && heightFromBottom < biome.endHeight) {
                return biome;
            }
        }
        
        // Fallback to last biome
        return this.biomes[this.biomes.length - 1];
    }

    /**
     * Get all biomes (for UI/debugging)
     */
    getAllBiomes(): Biome[] {
        return this.biomes;
    }

    /**
     * Get platform style properties for a biome
     * Includes neumorphic shading properties
     */
    getPlatformStyle(biome: Biome): {
        color: number;
        highlightColor: number; // Lighter version for neumorphic highlight
        shadowColor: number; // Darker version for neumorphic shadow
        texture?: string;
        roughness: number; // 0-1, affects bounce/slide
        chamferRadius: number; // Corner rounding radius
    } {
        const primary = biome.colorPalette.primary;
        
        // Generate highlight (lighter) and shadow (darker) for neumorphic effect
        const highlightColor = this.lightenColor(primary, 0.15); // 15% lighter
        const shadowColor = this.darkenColor(primary, 0.2); // 20% darker
        
        switch (biome.platformStyle) {
            case 'wood':
                return {
                    color: primary,
                    highlightColor,
                    shadowColor,
                    roughness: 0.7,
                    chamferRadius: 8 // Slight rounding
                };
            case 'stone':
                return {
                    color: primary,
                    highlightColor,
                    shadowColor,
                    roughness: 0.9,
                    chamferRadius: 6 // Less rounding for stone
                };
            case 'cloud':
                return {
                    color: primary,
                    highlightColor,
                    shadowColor,
                    roughness: 0.3,
                    chamferRadius: 12 // More rounding for soft clouds
                };
            case 'crystal':
                return {
                    color: primary,
                    highlightColor,
                    shadowColor,
                    roughness: 0.5,
                    chamferRadius: 10 // Medium rounding
                };
            case 'organic':
                return {
                    color: primary,
                    highlightColor,
                    shadowColor,
                    roughness: 0.7,
                    chamferRadius: 10
                };
            default:
                return {
                    color: primary,
                    highlightColor,
                    shadowColor,
                    roughness: 0.7,
                    chamferRadius: 8
                };
        }
    }

    /**
     * Lighten a color by a percentage (0-1)
     */
    private lightenColor(color: number, amount: number): number {
        const r = ((color >> 16) & 0xFF) + Math.floor(0xFF * amount);
        const g = ((color >> 8) & 0xFF) + Math.floor(0xFF * amount);
        const b = (color & 0xFF) + Math.floor(0xFF * amount);
        return ((Math.min(255, r) << 16) | (Math.min(255, g) << 8) | Math.min(255, b));
    }

    /**
     * Darken a color by a percentage (0-1)
     */
    private darkenColor(color: number, amount: number): number {
        const r = Math.floor(((color >> 16) & 0xFF) * (1 - amount));
        const g = Math.floor(((color >> 8) & 0xFF) * (1 - amount));
        const b = Math.floor((color & 0xFF) * (1 - amount));
        return ((r << 16) | (g << 8) | b);
    }

    /**
     * Get difficulty-adjusted jump height for a biome
     */
    getAdjustedJumpHeight(baseMin: number, baseMax: number, biome: Biome): { min: number; max: number } {
        return {
            min: baseMin * biome.difficultyMultiplier,
            max: baseMax * biome.difficultyMultiplier
        };
    }

    /**
     * Choose a platform type based on biome preferences
     */
    choosePlatformType(biome: Biome): string {
        const types = biome.platformTypes;
        return this.rng.choice(types);
    }
}

